import { apiClient } from './axios';
import type {
  SocialPost,
  TaggedPetSummary,
} from '@/pet/src/components/social/socialTypes';

/**
 * Initialize an upload for social post media (image/video).
 * Uses documents folder with a 'social/' prefix so backend accepts it; use getSignedUrl(key) for display.
 */
export async function initSocialMediaUpload(data: {
  filename: string;
  contentType: string;
}): Promise<{ uploadUrl: string; key: string; bucket?: string }> {
  const safeName = data.filename.replace(/[^a-zA-Z0-9._-]/g, '_');
  const filename = `social/${Date.now()}-${safeName}`;
  const response = await apiClient.post<{
    uploadUrl: string;
    key: string;
    bucket?: string;
  }>('/documents/upload-init', {
    filename,
    contentType: data.contentType,
    folder: 'documents',
    ttlSeconds: 900,
  });
  // Backend may return a relative uploadUrl — resolve against API base so fetch works on deployed envs
  let uploadUrl = response.data.uploadUrl;
  if (uploadUrl.startsWith('/')) {
    const base = apiClient.defaults.baseURL ?? '';
    uploadUrl = `${base}${uploadUrl}`;
  }
  return { ...response.data, uploadUrl };
}

/** Upload a file to the social media upload URL (uses apiClient for auth). */
export async function uploadSocialMedia(
  uploadUrl: string,
  file: File
): Promise<void> {
  // If URL is relative, use apiClient.put which handles base URL + auth
  if (uploadUrl.startsWith('/') || uploadUrl.startsWith(apiClient.defaults.baseURL ?? '___')) {
    const path = uploadUrl.startsWith('/') ? uploadUrl : uploadUrl.replace(apiClient.defaults.baseURL ?? '', '');
    await apiClient.put(path, file, {
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    return;
  }
  // External URL (e.g. presigned S3) — use fetch directly
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
}

export interface CreatePostInput {
  caption: string;
  images: string[];
  taggedPetIds: string[];
  taggedPetSummaries?: TaggedPetSummary[];
  location?: string;
  fromPetParks?: boolean;
  visibility?:
    | 'public'
    | 'followers'
    | 'friends'
    | 'friends-of-friends'
    | 'private';
  sharedPlatforms?: ('instagram' | 'facebook' | 'tiktok' | 'x')[];
  /**
   * Owner user id (e.g. Clerk id). Sent so the backend can store it when it matches auth;
   * avoids profile/feed showing no posts when backend would otherwise use a different id.
   * Server should validate ownerId === auth user and ignore or reject if not.
   */
  ownerId?: string;
  /** Display name for the post owner (shown in feed and profile). */
  ownerName?: string;
  /** Avatar URL for the post owner. */
  ownerAvatar?: string;
}

/** Build create-post body. Send ownerId so backend can store it when it matches auth (avoids profile/feed mismatch when backend would otherwise use a different id). */
function buildCreatePostBody(data: CreatePostInput): CreatePostInput {
  return { ...data };
}

export async function createPost(
  data: CreatePostInput
): Promise<{ post: SocialPost }> {
  const body = buildCreatePostBody(data);
  const res = await apiClient.post<Record<string, unknown>>('/social/posts', body);
  const raw = res.data;
  // Backend may return { post: {...} } or the post object directly
  const post = (raw.post ?? raw) as SocialPost;
  return { post };
}

/** Normalize feed post: isLiked/likes and owner fields (backend may use snake_case or different names). */
function normalizeFeedPost(raw: Record<string, unknown>): SocialPost {
  const post = raw as SocialPost;

  // isLiked: backend may return 'isLiked', 'liked', etc.
  const isLiked =
    post.isLiked === true ||
    raw.liked === true ||
    raw.is_liked === true ||
    raw.likedByViewer === true ||
    raw.viewerHasLiked === true;

  // likes: backend may return 'likes' or 'likeCount'
  const likes =
    typeof post.likes === 'number'
      ? post.likes
      : typeof raw.likeCount === 'number'
        ? (raw.likeCount as number)
        : 0;

  // isSaved: backend may return 'isSaved' or 'saved'
  const isSaved =
    post.isSaved === true || raw.saved === true;

  // ownerId: resolve from multiple possible field names, including nested 'owner' object
  const ownerObj = raw.owner as Record<string, unknown> | null | undefined;
  const ownerId =
    typeof post.ownerId === 'string' && post.ownerId
      ? post.ownerId
      : typeof raw.ownerClerkId === 'string'
        ? (raw.ownerClerkId as string)
        : typeof ownerObj?.clerkId === 'string'
          ? (ownerObj.clerkId as string)
          : typeof raw.owner_id === 'string'
            ? (raw.owner_id as string)
            : post.ownerId;

  // ownerName: resolve from flat field or nested 'owner' object
  const ownerName =
    typeof post.ownerName === 'string'
      ? post.ownerName
      : typeof ownerObj?.fullName === 'string'
        ? (ownerObj.fullName as string)
        : typeof raw.owner_name === 'string'
          ? (raw.owner_name as string)
          : null;

  // ownerAvatar: resolve from flat field or nested 'owner' object
  const ownerAvatar =
    typeof post.ownerAvatar === 'string'
      ? post.ownerAvatar
      : typeof ownerObj?.imageUrl === 'string'
        ? (ownerObj.imageUrl as string)
        : typeof raw.owner_avatar === 'string'
          ? (raw.owner_avatar as string)
          : null;

  // comments: backend may return 'comments' or 'recentComments'; normalize field names
  const rawComments = Array.isArray(post.comments)
    ? post.comments
    : Array.isArray(raw.recentComments)
      ? (raw.recentComments as Record<string, unknown>[])
      : [];
  const comments = rawComments.map((c: Record<string, unknown>) => ({
    id: (c.id as string) ?? '',
    authorId: (c.authorId as string) ?? (c.authorClerkId as string) ?? '',
    author: (c.author as string) ?? (c.authorName as string) ?? 'User',
    avatar: (c.avatar as string) ?? (c.authorAvatar as string) ?? '',
    text: (c.text as string) ?? '',
    timestamp: (c.timestamp as string) ?? (c.createdAt as string) ?? '',
  }));

  // taggedPets: backend may return 'taggedPets' or 'taggedPetSummaries'
  const taggedPets = Array.isArray(post.taggedPets)
    ? post.taggedPets
    : Array.isArray(raw.taggedPetSummaries)
      ? (raw.taggedPetSummaries as SocialPost['taggedPets'])
      : [];

  // taggedPetIds: may be null from DB JSON column
  const taggedPetIds = Array.isArray(post.taggedPetIds)
    ? post.taggedPetIds
    : [];

  // images: backend may return 'images' or 'imageKeys'
  const images = Array.isArray(post.images)
    ? post.images
    : Array.isArray(raw.imageKeys)
      ? (raw.imageKeys as string[])
      : [];

  // timestamp
  const rawTimestamp = post.timestamp ?? (raw.createdAt as string | number | undefined) ?? '';
  const timestamp = typeof rawTimestamp === 'number' ? new Date(rawTimestamp).toISOString() : String(rawTimestamp);

  // createdAt as ms epoch for edit window checks
  let createdAt: number | undefined = typeof post.createdAt === 'number' ? post.createdAt : undefined;
  if (createdAt == null && typeof raw.createdAt === 'string') {
    const parsed = new Date(raw.createdAt as string).getTime();
    if (!isNaN(parsed)) createdAt = parsed;
  }
  if (createdAt == null && typeof rawTimestamp === 'string' && rawTimestamp) {
    const parsed = new Date(rawTimestamp).getTime();
    if (!isNaN(parsed)) createdAt = parsed;
  }

  return {
    ...post,
    isLiked,
    likes,
    isSaved,
    ownerId,
    ownerName,
    ownerAvatar,
    comments,
    taggedPets,
    taggedPetIds,
    images,
    timestamp,
    createdAt,
    tags: post.tags ?? [],
  };
}

export async function getFeed(params?: {
  limit?: number;
  cursor?: string;
  scope?: 'all' | 'following' | 'myPets';
  /** Fetch posts by a specific user (for profile view). Allowed if public or viewer follows. */
  ownerId?: string;
  /** Fetch posts where this pet is tagged (for profile Tagged tab). */
  taggedPetId?: string;
}): Promise<{ posts: SocialPost[]; nextCursor?: string }> {
  const searchParams = new URLSearchParams();
  if (params?.limit != null) searchParams.set('limit', String(params.limit));
  if (params?.cursor) searchParams.set('cursor', params.cursor);
  if (params?.scope) searchParams.set('scope', params.scope);
  if (params?.ownerId) {
    searchParams.set('ownerId', params.ownerId);
    searchParams.set('userId', params.ownerId);
  }
  if (params?.taggedPetId) searchParams.set('taggedPetId', params.taggedPetId);
  const qs = searchParams.toString();
  const res = await apiClient.get<{ posts?: SocialPost[]; items?: SocialPost[]; nextCursor?: string }>(
    `/social/posts${qs ? `?${qs}` : ''}`
  );
  // Backend may return 'posts' or 'items' depending on deployed version
  const rawPosts = res.data.posts ?? res.data.items ?? [];
  const posts = rawPosts.map((p) =>
    normalizeFeedPost(p as unknown as Record<string, unknown>)
  );
  return { posts, nextCursor: res.data.nextCursor };
}

export async function getPostById(id: string): Promise<{ post: SocialPost }> {
  const res = await apiClient.get<{ post: SocialPost }>(`/social/posts/${id}`);
  const post = normalizeFeedPost(
    (res.data.post ?? {}) as unknown as Record<string, unknown>
  );
  return { ...res.data, post };
}

/** Pet that can be tagged in a post (own + followers' pets). */
export interface TaggablePet {
  id: string;
  name: string;
  species?: string;
  avatar: string;
  imageUrl?: string;
  photoKey?: string;
  ownerClerkId?: string;
  ownerName?: string;
}

export async function getTaggablePets(params?: {
  name?: string;
}): Promise<{ pets: TaggablePet[] }> {
  const searchParams = new URLSearchParams();
  if (params?.name?.trim()) searchParams.set('name', params.name.trim());
  const qs = searchParams.toString();
  const res = await apiClient.get<{ pets: TaggablePet[] }>(
    `/social/taggable-pets${qs ? `?${qs}` : ''}`
  );
  return res.data;
}

export interface UpdatePostInput {
  caption?: string;
  images?: string[];
  taggedPetIds?: string[];
  taggedPetSummaries?: TaggedPetSummary[];
  location?: string;
  visibility?: 'public' | 'followers' | 'friends' | 'friends-of-friends' | 'private';
}

export async function updatePost(
  id: string,
  data: UpdatePostInput
): Promise<{ post: SocialPost }> {
  const res = await apiClient.patch<{ post: SocialPost }>(
    `/social/posts/${id}`,
    data
  );
  return res.data;
}

export async function deletePost(id: string): Promise<void> {
  await apiClient.delete(`/social/posts/${id}`);
}

export async function likePost(
  id: string
): Promise<{ liked: boolean; likes: number }> {
  const res = await apiClient.post<{ liked: boolean; likes: number }>(
    `/social/posts/${id}/like`
  );
  return res.data;
}

export async function unlikePost(
  id: string
): Promise<{ liked: boolean; likes: number }> {
  const res = await apiClient.delete<{ liked: boolean; likes: number }>(
    `/social/posts/${id}/like`
  );
  return res.data;
}

export interface AddCommentResponse {
  comment: {
    id: string;
    authorId: string;
    author: string;
    avatar: string;
    text: string;
    timestamp: string;
  };
}

/** Normalize comment from API (backend may wrap in .data or use different keys). */
function normalizeComment(raw: unknown): AddCommentResponse['comment'] {
  const o =
    raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    id: typeof o.id === 'string' ? o.id : `c-${Date.now()}`,
    authorId:
      typeof o.authorId === 'string'
        ? o.authorId
        : typeof o.authorClerkId === 'string'
          ? (o.authorClerkId as string)
          : '',
    author:
      typeof o.author === 'string'
        ? o.author
        : ((o.authorName as string) ?? 'You'),
    avatar:
      typeof o.avatar === 'string'
        ? o.avatar
        : typeof o.authorAvatar === 'string'
          ? (o.authorAvatar as string)
          : '',
    text: typeof o.text === 'string' ? o.text : ((o.content as string) ?? ''),
    timestamp:
      typeof o.timestamp === 'string'
        ? o.timestamp
        : ((o.createdAt as string) ?? new Date().toISOString()),
  };
}

export async function addComment(
  postId: string,
  text: string
): Promise<AddCommentResponse> {
  const res = await apiClient.post<unknown>(
    `/social/posts/${postId}/comments`,
    { text }
  );
  const data = res.data as Record<string, unknown>;
  const rawComment = data?.comment ?? data?.data ?? data;
  const comment = normalizeComment(rawComment);
  return { comment };
}

export async function deleteComment(
  postId: string,
  commentId: string
): Promise<void> {
  await apiClient.delete(`/social/posts/${postId}/comments/${commentId}`);
}

export async function savePost(id: string): Promise<{ saved: boolean }> {
  const res = await apiClient.post<{ saved: boolean }>(
    `/social/posts/${id}/save`
  );
  return res.data;
}

export async function unsavePost(id: string): Promise<{ saved: boolean }> {
  const res = await apiClient.delete<{ saved: boolean }>(
    `/social/posts/${id}/save`
  );
  return res.data;
}

// --- Follow / Unfollow ---
export type FollowingType = 'USER' | 'PET';

export async function follow(data: {
  followingId: string;
  followingType: FollowingType;
}): Promise<{ following: boolean }> {
  const res = await apiClient.post<{ following: boolean }>(
    '/social/follow',
    data
  );
  return res.data;
}

export async function unfollow(
  followingType: FollowingType,
  followingId: string
): Promise<{ following: boolean }> {
  const res = await apiClient.delete<{ following: boolean }>(
    `/social/follow/${followingType}/${encodeURIComponent(followingId)}`
  );
  return res.data;
}

export async function checkFollowStatus(
  followingType: FollowingType,
  followingId: string
): Promise<{ following: boolean }> {
  const res = await apiClient.get<{ following: boolean }>(
    `/social/follow/${followingType}/${encodeURIComponent(followingId)}`
  );
  return res.data;
}

export interface FollowerOrFollowingItem {
  id: string;
  type: 'USER' | 'PET';
  name: string | null;
  avatar?: string | null;
  followedAt: string;
}

/** Backend may return raw follow records or enriched items with name/avatar */
function mapFollowersResponse(raw: {
  followers?: Array<{
    followerClerkId?: string;
    followingId?: string;
    followingType?: string;
    createdAt?: string;
    id?: string;
    type?: 'USER' | 'PET';
    name?: string | null;
    avatar?: string | null;
    followedAt?: string;
  }>;
  nextCursor?: string;
  total?: number;
}): {
  followers: FollowerOrFollowingItem[];
  nextCursor?: string;
  total: number;
} {
  const list = raw.followers ?? [];
  const followers: FollowerOrFollowingItem[] = list.map((f) => {
    const enriched = f as { name?: string | null; avatar?: string | null };
    return {
      id: f.id ?? f.followerClerkId ?? f.followingId ?? '',
      type: (f.type ?? (f.followingType === 'PET' ? 'PET' : 'USER')) as
        | 'USER'
        | 'PET',
      name: enriched.name ?? null,
      avatar: enriched.avatar ?? null,
      followedAt: f.followedAt ?? f.createdAt ?? new Date().toISOString(),
    };
  });
  return {
    followers,
    nextCursor: raw.nextCursor,
    total: raw.total ?? followers.length,
  };
}

export async function getFollowers(params?: {
  id?: string;
  type?: FollowingType;
  limit?: number;
  cursor?: string;
}): Promise<{
  followers: FollowerOrFollowingItem[];
  nextCursor?: string;
  total: number;
}> {
  const searchParams = new URLSearchParams();
  if (params?.type) searchParams.set('type', params.type);
  if (params?.limit != null) searchParams.set('limit', String(params.limit));
  if (params?.cursor) searchParams.set('cursor', params.cursor);
  const qs = searchParams.toString();
  const path = params?.id
    ? `/social/followers/${params.id}`
    : '/social/followers';
  const res = await apiClient.get<{
    followers?: Array<{
      followerClerkId?: string;
      followingId?: string;
      followingType?: string;
      createdAt?: string;
    }>;
    nextCursor?: string;
    total?: number;
  }>(`${path}${qs ? `?${qs}` : ''}`);
  return mapFollowersResponse(res.data);
}

/** Backend may return raw follow records or enriched items with name/avatar */
function mapFollowingResponse(raw: {
  following?: Array<{
    followingId?: string;
    followingType?: string;
    createdAt?: string;
    id?: string;
    type?: 'USER' | 'PET';
    name?: string | null;
    avatar?: string | null;
    followedAt?: string;
  }>;
  nextCursor?: string;
  total?: number;
}): {
  following: FollowerOrFollowingItem[];
  nextCursor?: string;
  total: number;
} {
  const list = raw.following ?? [];
  const following: FollowerOrFollowingItem[] = list.map((f) => {
    const enriched = f as { name?: string | null; avatar?: string | null };
    return {
      id: f.id ?? f.followingId ?? '',
      type: (f.type ?? (f.followingType === 'PET' ? 'PET' : 'USER')) as
        | 'USER'
        | 'PET',
      name: enriched.name ?? null,
      avatar: enriched.avatar ?? null,
      followedAt: f.followedAt ?? f.createdAt ?? new Date().toISOString(),
    };
  });
  return {
    following,
    nextCursor: raw.nextCursor,
    total: raw.total ?? following.length,
  };
}

export async function getFollowing(params?: {
  id?: string;
  limit?: number;
  cursor?: string;
}): Promise<{
  following: FollowerOrFollowingItem[];
  nextCursor?: string;
  total: number;
}> {
  const searchParams = new URLSearchParams();
  if (params?.limit != null) searchParams.set('limit', String(params.limit));
  if (params?.cursor) searchParams.set('cursor', params.cursor);
  const qs = searchParams.toString();
  const path = params?.id
    ? `/social/following/${params.id}`
    : '/social/following';
  const res = await apiClient.get<{
    following?: Array<{
      followingId?: string;
      followingType?: string;
      createdAt?: string;
    }>;
    nextCursor?: string;
    total?: number;
  }>(`${path}${qs ? `?${qs}` : ''}`);
  return mapFollowingResponse(res.data);
}

// --- Notifications ---
export type SocialNotificationType =
  | 'like'
  | 'comment'
  | 'follow'
  | 'follow_request'
  | 'follow_accepted'
  | 'follow_declined';

export interface SocialNotificationItem {
  id: string;
  type: SocialNotificationType;
  triggeredBy: string;
  triggeredByName: string;
  triggeredByAvatar?: string | null;
  referenceId: string | null;
  metadata: Record<string, unknown> | null;
  read: boolean;
  createdAt: string;
}

export async function getNotifications(params?: {
  limit?: number;
  cursor?: string;
}): Promise<{
  notifications: SocialNotificationItem[];
  unreadCount: number;
  nextCursor?: string;
}> {
  const searchParams = new URLSearchParams();
  if (params?.limit != null) searchParams.set('limit', String(params.limit));
  if (params?.cursor) searchParams.set('cursor', params.cursor);
  const qs = searchParams.toString();
  try {
    const res = await apiClient.get<{
      notifications?: SocialNotificationItem[];
      unreadCount?: number;
      nextCursor?: string;
      data?: { notifications?: SocialNotificationItem[]; unreadCount?: number };
    }>(`/social/notifications${qs ? `?${qs}` : ''}`);
    const raw = res.data;
    const data = raw?.data ?? raw;
    const list = Array.isArray(data?.notifications) ? data.notifications : [];
    const unreadCount =
      typeof data?.unreadCount === 'number' ? data.unreadCount : 0;
    return {
      notifications: list,
      unreadCount,
      nextCursor: raw?.nextCursor ?? data?.nextCursor,
    };
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response
      ?.status;
    if (status === 404 || status === 502 || status === 503) {
      return { notifications: [], unreadCount: 0 };
    }
    throw err;
  }
}

export async function markNotificationRead(
  id: string
): Promise<{ id: string; read: true }> {
  const res = await apiClient.patch<{ id: string; read: true }>(
    `/social/notifications/${id}/read`
  );
  return res.data;
}

export async function markAllNotificationsRead(): Promise<{
  markedAllRead: boolean;
}> {
  const res = await apiClient.patch<{ markedAllRead: boolean }>(
    '/social/notifications/read-all'
  );
  return res.data;
}

// --- Direct Messages ---

export interface DmConversation {
  partnerId: string;
  partnerName: string | null;
  partnerAvatar: string | null;
  lastMessage: string;
  lastMessageAt: string;
  lastMessageFromMe: boolean;
  unreadCount: number;
}

export interface DmMessage {
  id: string;
  fromId: string;
  toId: string;
  text: string;
  readAt: string | null;
  createdAt: string;
  fromMe: boolean;
}

export async function getDmConversations(): Promise<DmConversation[]> {
  const res = await apiClient.get<{ conversations: DmConversation[] }>(
    '/social/dm/conversations'
  );
  return res.data.conversations ?? [];
}

export async function getDmMessages(
  userId: string,
  params?: { limit?: number; cursor?: string }
): Promise<{ messages: DmMessage[]; nextCursor?: string }> {
  const searchParams = new URLSearchParams();
  if (params?.limit != null) searchParams.set('limit', String(params.limit));
  if (params?.cursor) searchParams.set('cursor', params.cursor);
  const qs = searchParams.toString();
  const res = await apiClient.get<{
    messages: DmMessage[];
    nextCursor?: string;
  }>(`/social/dm/${userId}${qs ? `?${qs}` : ''}`);
  return { messages: res.data.messages ?? [], nextCursor: res.data.nextCursor };
}

// --- Report Post ---
export type ReportReason = 'spam' | 'inappropriate' | 'harassment' | 'misinformation' | 'offensive' | 'other';

export async function reportPost(
  postId: string,
  data: { reason: ReportReason; description?: string }
): Promise<{ ok: boolean }> {
  const res = await apiClient.post<{ ok: boolean }>(
    `/social/posts/${postId}/report`,
    data
  );
  return res.data;
}

export async function sendDm(userId: string, text: string): Promise<DmMessage> {
  const res = await apiClient.post<{ message: DmMessage }>(
    `/social/dm/${userId}`,
    { text }
  );
  return res.data.message;
}
